import type { KeyPoseId, KeyPoseRecord, KeyPoseStamp } from './types';
import type { KeyPoseEditingState } from './types';

export function isIntervalKeyPoseId(id: string) {
  return id.startsWith('interval-');
}

export function resolveKeyPoseStamp({
  id,
  stamp,
}: {
  id?: string;
  stamp?: KeyPoseStamp;
}): KeyPoseStamp {
  if (stamp === 'interval' || (id && isIntervalKeyPoseId(id))) {
    return 'interval';
  }

  return 'user';
}

export function isIntervalKeyPoseRecord(
  record: Pick<KeyPoseRecord, 'id' | 'stamp'>,
) {
  return resolveKeyPoseStamp(record) === 'interval';
}

export function isUserKeyPoseRecord(
  record: Pick<KeyPoseRecord, 'id' | 'stamp'>,
) {
  return !isIntervalKeyPoseRecord(record);
}

export function isKeyPoseFrameEditableForLiveActor(
  record: Pick<KeyPoseRecord, 'id' | 'stamp' | 'hidden'>,
) {
  return isUserKeyPoseRecord(record) && !record.hidden;
}

export function isKeyPoseSnappedAtPlaybackFrame(
  record: Pick<KeyPoseRecord, 'id' | 'stamp' | 'hidden'>,
  hasVisibleUserKeyPoseGhosts: boolean,
) {
  return !hasVisibleUserKeyPoseGhosts && isUserKeyPoseRecord(record);
}

export function isKeyPoseEditableAtPlaybackFrame(
  record: Pick<KeyPoseRecord, 'id' | 'stamp' | 'hidden'>,
  hasVisibleUserKeyPoseGhosts: boolean,
) {
  return (
    isKeyPoseFrameEditableForLiveActor(record) ||
    isKeyPoseSnappedAtPlaybackFrame(record, hasVisibleUserKeyPoseGhosts)
  );
}

export function hasKeyPoseBoneSelection(
  editing: Pick<
    KeyPoseEditingState,
    'mode' | 'selectedFkJointId' | 'selectedIkEffectorId'
  >,
) {
  return Boolean(
    editing.mode === 'fk'
      ? editing.selectedFkJointId
      : editing.selectedIkEffectorId,
  );
}

export function isKeyPoseGizmoEditingActive(
  keyPose: Pick<KeyPoseRecord, 'id' | 'stamp' | 'hidden'> | null,
  editing: Pick<
    KeyPoseEditingState,
    'mode' | 'selectedFkJointId' | 'selectedIkEffectorId'
  >,
  hasVisibleUserKeyPoseGhosts = true,
) {
  if (!keyPose) {
    return false;
  }

  if (isKeyPoseEditableAtPlaybackFrame(keyPose, hasVisibleUserKeyPoseGhosts)) {
    return true;
  }

  return (
    isIntervalKeyPoseRecord(keyPose) &&
    !keyPose.hidden &&
    hasKeyPoseBoneSelection(editing)
  );
}

export function isKeyPoseRenderableInScene(
  record: Pick<KeyPoseRecord, 'id' | 'stamp' | 'hidden'>,
  hasVisibleUserKeyPoseGhosts: boolean,
) {
  if (isIntervalKeyPoseRecord(record)) {
    return hasVisibleUserKeyPoseGhosts && !record.hidden;
  }

  return true;
}

export function hasVisibleUserKeyPoseGhostsForReference(
  state: KeyPoseSelectorState,
  referenceName: string,
) {
  const referenceState = state.referencesByName[referenceName];
  if (!referenceState) {
    return false;
  }

  return referenceState.keyPoseIds.some((keyPoseId) => {
    const keyPose = state.keyPosesById[keyPoseId];
    return keyPose && isUserKeyPoseRecord(keyPose) && !keyPose.hidden;
  });
}

export function shouldRenderKeyPoseGhostAtPlaybackFrame({
  keyPose,
  keyPosePlaybackFrame,
  currentPlaybackFrame,
  liveActorReplacementKeyPoseId,
  draggingKeyPoseId = null,
  hasVisibleUserKeyPoseGhosts = true,
}: {
  keyPose: Pick<KeyPoseRecord, 'id' | 'stamp' | 'hidden'>;
  keyPosePlaybackFrame: number;
  currentPlaybackFrame: number;
  liveActorReplacementKeyPoseId: KeyPoseId | null;
  draggingKeyPoseId?: KeyPoseId | null;
  hasVisibleUserKeyPoseGhosts?: boolean;
}) {
  if (keyPosePlaybackFrame !== currentPlaybackFrame) {
    if (
      !hasVisibleUserKeyPoseGhosts &&
      isUserKeyPoseRecord(keyPose) &&
      keyPose.hidden
    ) {
      return false;
    }

    return true;
  }

  if (draggingKeyPoseId && keyPose.id === draggingKeyPoseId) {
    return true;
  }

  if (
    keyPose.id === liveActorReplacementKeyPoseId &&
    isKeyPoseEditableAtPlaybackFrame(keyPose, hasVisibleUserKeyPoseGhosts)
  ) {
    return true;
  }

  return isKeyPoseSnappedAtPlaybackFrame(keyPose, hasVisibleUserKeyPoseGhosts);
}

export function shouldReplaceIntervalStampWithUserKeyPose(
  incomingStamp: KeyPoseStamp | undefined,
  existingKeyPose: Pick<KeyPoseRecord, 'id' | 'stamp'> | null | undefined,
) {
  return (
    (incomingStamp ?? 'user') === 'user' &&
    existingKeyPose !== null &&
    existingKeyPose !== undefined &&
    isIntervalKeyPoseRecord(existingKeyPose)
  );
}

export function createIntervalKeyPoseId(frame: number) {
  return `interval-${Math.max(Math.round(frame), 0)}`;
}

interface KeyPoseSelectorReferenceState {
  keyPoseIds: readonly KeyPoseId[];
}

interface KeyPoseSelectorState<TRecord extends KeyPoseRecord = KeyPoseRecord> {
  referencesByName: Readonly<
    Record<string, KeyPoseSelectorReferenceState | undefined>
  >;
  keyPosesById: Readonly<Record<KeyPoseId, TRecord | undefined>>;
}

export interface KeyPoseTimelineItem {
  id: KeyPoseId;
  frame: number;
  hidden: boolean;
  stamp: KeyPoseStamp;
}

export interface KeyPoseApiPayloadItem {
  id: KeyPoseId;
  frame: number;
  localJointRot: number[][];
  rootPosition: number[];
  smoothRoot2d: number[];
}

export function selectKeyPoseRecordsForReference<TRecord extends KeyPoseRecord>(
  state: KeyPoseSelectorState<TRecord>,
  referenceName: string | null | undefined,
) {
  if (!referenceName) {
    return [] as TRecord[];
  }

  const referenceState = state.referencesByName[referenceName];
  if (!referenceState) {
    return [] as TRecord[];
  }

  return referenceState.keyPoseIds
    .flatMap((keyPoseId) => {
      const keyPose = state.keyPosesById[keyPoseId];

      return keyPose ? [keyPose] : [];
    })
    .sort((a, b) => a.frame - b.frame);
}

export function selectUserKeyPoseRecordsForReference<
  TRecord extends KeyPoseRecord,
>(
  state: KeyPoseSelectorState<TRecord>,
  referenceName: string | null | undefined,
) {
  return selectKeyPoseRecordsForReference(state, referenceName).filter(
    isUserKeyPoseRecord,
  );
}

export function selectIntervalKeyPoseRecordsForReference<
  TRecord extends KeyPoseRecord,
>(
  state: KeyPoseSelectorState<TRecord>,
  referenceName: string | null | undefined,
) {
  return selectKeyPoseRecordsForReference(state, referenceName).filter(
    isIntervalKeyPoseRecord,
  );
}

export function selectKeyPoseTimelineItems(
  state: KeyPoseSelectorState,
  referenceName: string | null | undefined,
): KeyPoseTimelineItem[] {
  return selectKeyPoseRecordsForReference(state, referenceName).map(
    (keyPose) => ({
      id: keyPose.id,
      frame: keyPose.frame,
      hidden: Boolean(keyPose.hidden),
      stamp: resolveKeyPoseStamp(keyPose),
    }),
  );
}

function mapKeyPoseRecordToApiPayload(
  keyPose: KeyPoseRecord,
): KeyPoseApiPayloadItem {
  return {
    id: keyPose.id,
    frame: keyPose.frame,
    localJointRot: keyPose.sourcePose.localJointRot,
    rootPosition: keyPose.sourcePose.rootPosition,
    smoothRoot2d: keyPose.sourcePose.smoothRoot2d,
  };
}

export function selectUserKeyPoseApiPayloadForReference(
  state: KeyPoseSelectorState,
  referenceName: string | null | undefined,
): KeyPoseApiPayloadItem[] {
  return selectUserKeyPoseRecordsForReference(state, referenceName).map(
    mapKeyPoseRecordToApiPayload,
  );
}

export function selectIntervalStampApiPayloadForReference(
  state: KeyPoseSelectorState,
  referenceName: string | null | undefined,
): KeyPoseApiPayloadItem[] {
  return selectIntervalKeyPoseRecordsForReference(state, referenceName).map(
    mapKeyPoseRecordToApiPayload,
  );
}

export function selectKeyPoseRecordAtFrame<TRecord extends KeyPoseRecord>(
  state: KeyPoseSelectorState<TRecord>,
  referenceName: string | null | undefined,
  frame: number,
) {
  const normalizedFrame = Math.max(Math.round(frame), 0);

  return (
    selectKeyPoseRecordsForReference(state, referenceName).find(
      (keyPose) => keyPose.frame === normalizedFrame,
    ) ?? null
  );
}

export function selectKeyPoseApiPayloadForReference(
  state: KeyPoseSelectorState,
  referenceName: string | null | undefined,
): KeyPoseApiPayloadItem[] {
  return selectKeyPoseRecordsForReference(state, referenceName).map(
    mapKeyPoseRecordToApiPayload,
  );
}
